// components/ReservationItem.jsx
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiUser,
  FiCalendar,
  FiClock,
  FiDollarSign,
  FiCheckCircle,
  FiEdit2,
  FiXCircle,
  FiRefreshCw,
} from "react-icons/fi";
import { toast } from "react-toastify";
import useAuthStore from "../store/authStore";
import AssignServiceModal from "./admin/AssignServiceModal";
import axiosToken from "../api/axiosClient";
import ReservationServiceCalendar from "./ReservationServiceCalendar";
import { isManager } from "../utils/permissions";
import { getClientName } from "../utils/clientInfo";
import { formatSalonDateTime } from "../utils/salonTime";

function ReservationItem({
  item,
  withAssign = false,
  temporaryReservation,
  onUpdated,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModal, setIsCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isRescheduleModal, setIsRescheduleModal] = useState(false);
  const [newDate, setNewDate] = useState(null);
  const [isRescheduleLoading, setIsRescheduleLoading] = useState(false);
  const [isCompleteModal, setIsCompleteModal] = useState(false);
  const [isCompleteLoading, setIsCompleteLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(item.id == temporaryReservation);
  const { user } = useAuthStore();

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedRdv, setSelectedRdv] = useState({});

  if (!item || !item.id) {
    return (
      <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200">
        <p className="font-nunito text-sm text-red-600">
          Données de réservation incomplètes
        </p>
      </div>
    );
  }

  // Heure du rendez-vous = heure du salon (voir utils/salonTime.js)
  const formatDate = (dateString) =>
    formatSalonDateTime(dateString, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatShortDate = (dateString) =>
    formatSalonDateTime(dateString, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "CONFIRMED":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "CANCELLED":
        return "bg-rose-100 text-rose-800 border-rose-200";
      case "COMPLETED":
        return "bg-sky-100 text-sky-800 border-sky-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "CONFIRMED":
        return "✓";
      case "COMPLETED":
        return "✓";
      case "PENDING":
        return "⏳";
      case "CANCELLED":
        return "✗";
      default:
        return "•";
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      PENDING: "En attente",
      CONFIRMED: "Confirmé",
      CANCELLED: "Annulé",
      COMPLETED: "Terminé",
    };
    return labels[status] || status;
  };

  const handleComplete = async () => {
    try {
      setIsCompleteLoading(true);
      await axiosToken.put(`/reservation/${item.id}/status`, {
        status: "COMPLETED",
      });
      item.status = "COMPLETED";
      toast.success("Rendez-vous terminé.");
    } catch (error) {
      toast.error("Une erreur est survenue, réessayez plus tard.");
    } finally {
      setIsCompleteLoading(false);
      setIsCompleteModal(false);
    }
  };

  const userIsManager = isManager(user);

  // Rendez-vous actif : il peut encore être reprogrammé
  const isActive = ["PENDING", "CONFIRMED"].includes(item.status);

  // Rendez-vous réglé (FedaPay, espèces, Mobile Money, carte cadeau ou
  // abonnement) : il ne peut pas être annulé, seulement reprogrammé.
  const isPaid =
    (item.payments || []).some((payment) => payment.status === "COMPLETED") ||
    !!item.giftCard ||
    !!item.subscription;

  const handleCancel = async () => {
    try {
      setIsCancelLoading(true);
      await axiosToken.put(`/reservation/${item.id}/cancel`, {
        reason: cancelReason.trim() || undefined,
      });
      item.status = "CANCELLED";
      item.assignments = [];
      toast.success("Rendez-vous annulé. Le créneau est de nouveau disponible.");
      setIsCancelModal(false);
      setCancelReason("");
      onUpdated?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          "Une erreur est survenue, réessayez plus tard.",
      );
    } finally {
      setIsCancelLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!newDate) return;
    try {
      setIsRescheduleLoading(true);
      const response = await axiosToken.put(
        `/reservation/${item.id}/reschedule`,
        { date: newDate },
      );
      const updated = response?.data?.data;
      if (updated?.date) item.date = updated.date;
      item.assignments = [];
      // Le message de l'API indique si le client a été prévenu par e-mail
      toast.success(
        response?.data?.message || "Rendez-vous reprogrammé avec succès.",
      );
      setIsRescheduleModal(false);
      setNewDate(null);
      onUpdated?.();
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          "Une erreur est survenue, réessayez plus tard.",
      );
    } finally {
      setIsRescheduleLoading(false);
    }
  };

  const totalServices = item.appointmentServices?.length || 0;
  const totalAmount = parseFloat(item.totalAmount || 0);

  return (
    <>
      <AssignServiceModal
        isAssignModalOpen={isAssignModalOpen}
        closeAssignModal={() => {
          setSelectedRdv(null);
          setIsAssignModalOpen(false);
        }}
        selectedRdv={selectedRdv}
      />

      {/* Carte de réservation principale */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="bg-white shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden group"
      >
        {/* En-tête compact */}
        <div
          className="p-4 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {item.id == temporaryReservation && (
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-100 to-transparent opacity-30 animate-pulse pointer-events-none" />
          )}

          <div className="flex items-center justify-between">
            {/* Informations essentielles */}
            <div className="flex items-center space-x-3">
              {/* Badge de statut */}
              <div
                className={`px-3 py-1.5 border ${getStatusColor(item.status)} flex items-center space-x-1.5`}
              >
                <span className="text-xs font-medium">
                  {getStatusIcon(item.status)}
                </span>
                <span className="font-nunito text-xs font-semibold whitespace-nowrap">
                  {getStatusLabel(item.status)}
                </span>
              </div>

              {/* Date et heure */}
              <div className="flex items-center space-x-2 text-gray-600">
                <FiCalendar className="w-4 h-4" />
                <span className="font-nunito text-sm font-medium">
                  {formatShortDate(item.date)}
                </span>
              </div>
            </div>

            {/* Montant et chevron */}
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="flex items-center space-x-1.5">
                  <span className="font-poppins font-bold text-lg text-gray-900">
                    {totalAmount.toFixed(0)}
                  </span>
                  <span className="font-nunito text-sm text-gray-500">
                    FCFA
                  </span>
                </div>
                <div className="font-nunito text-xs text-gray-500 mt-0.5">
                  {totalServices} service{totalServices > 1 ? "s" : ""}
                </div>
              </div>

              <button className="text-gray-400 hover:text-primary transition-colors p-1.5 hover:bg-gray-50">
                {isExpanded ? (
                  <FiChevronUp className="w-5 h-5" />
                ) : (
                  <FiChevronDown className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Détails expansibles */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-gray-100"
            >
              <div className="p-4">
                {/* Informations client */}
                <div className="mb-4 p-3 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <FiUser className="w-4 h-4 text-primary" />
                      <span className="font-poppins font-semibold text-sm text-gray-900">
                        Client
                      </span>
                    </div>
                    <span className="font-nunito text-sm text-gray-700">
                      {getClientName(item)}
                    </span>
                  </div>

                  {/* Services résumés */}
                  <div className="mt-3">
                    <div className="font-nunito text-xs text-gray-500 mb-2">
                      Services inclus :
                    </div>
                    <div className="space-y-1.5">
                      {item.appointmentServices
                        ?.slice(0, 2)
                        .map((service, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center"
                          >
                            <span className="font-nunito text-sm text-gray-700 truncate flex-1">
                              {service.service?.nom || "Service"}
                            </span>
                            <span className="font-poppins font-semibold text-sm text-gray-900">
                              {parseFloat(service.unitPrice || 0).toFixed(0)}{" "}
                              FCFA
                            </span>
                          </div>
                        ))}
                      {totalServices > 2 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsModalOpen(true);
                          }}
                          className="font-nunito text-xs text-primary hover:text-primary/80 font-medium mt-1"
                        >
                          +{totalServices - 2} autre
                          {totalServices - 2 > 1 ? "s" : ""} service
                          {totalServices - 2 > 1 ? "s" : ""}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assignations */}
                {userIsManager &&
                  item?.assignments?.length > 0 && (
                    <div className="mb-4">
                      <div className="font-nunito text-xs text-gray-500 mb-2">
                        Employés assignés
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {item.assignments.map((assignment) => (
                          <div
                            key={assignment.id}
                            className="px-2.5 py-1 bg-primary/10 text-primary flex items-center space-x-1"
                          >
                            <span className="font-nunito text-xs font-medium">
                              {assignment.employee?.prenom}{" "}
                              {assignment.employee?.nom?.charAt(0)}.
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-4 border-t border-gray-100 gap-3">
                  <div className="font-nunito text-xs text-gray-500 order-2 sm:order-1">
                    Créé le{" "}
                    {new Date(item.created_at).toLocaleDateString("fr-FR")}
                  </div>

                  <div className="flex flex-wrap gap-2 order-1 sm:order-2">
                    {userIsManager &&
                      item.status === "CONFIRMED" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsCompleteModal(true);
                          }}
                          className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-nunito font-semibold text-xs transition-colors duration-200 flex items-center space-x-1.5"
                        >
                          <FiCheckCircle className="w-3.5 h-3.5" />
                          <span>Terminer</span>
                        </button>
                      )}

                    {userIsManager && isActive && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setNewDate(null);
                          setIsRescheduleModal(true);
                        }}
                        className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white font-nunito font-semibold text-xs transition-colors duration-200 flex items-center space-x-1.5"
                      >
                        <FiRefreshCw className="w-3.5 h-3.5" />
                        <span>Reprogrammer</span>
                      </button>
                    )}

                    {userIsManager && isActive && !isPaid && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setCancelReason("");
                          setIsCancelModal(true);
                        }}
                        className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white font-nunito font-semibold text-xs transition-colors duration-200 flex items-center space-x-1.5"
                      >
                        <FiXCircle className="w-3.5 h-3.5" />
                        <span>Annuler</span>
                      </button>
                    )}

                    {withAssign &&
                      userIsManager &&
                      item.status !== "CANCELLED" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsAssignModalOpen(true);
                            setSelectedRdv(item);
                          }}
                          className="px-3 py-1.5 bg-primary hover:bg-primary/90 text-white font-nunito font-semibold text-xs transition-colors duration-200 flex items-center space-x-1.5"
                        >
                          <FiEdit2 className="w-3.5 h-3.5" />
                          <span>
                            {item.assignments?.length > 0
                              ? "Réassigner"
                              : "Assigner"}
                          </span>
                        </button>
                      )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsModalOpen(true);
                      }}
                      className="px-3 py-1.5 bg-gradient-to-r from-primary to-pink-600 hover:from-primary/90 hover:to-pink-600/90 text-white font-nunito font-semibold text-xs transition-all duration-200 flex-1 sm:flex-none"
                    >
                      Voir les détails
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Modal de détails - Version améliorée */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsModalOpen(false)}
            />

            {/* Modal Container */}
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
              >
                {/* Header fixe */}
                <div className="bg-gradient-to-r from-primary to-pink-600 p-6 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-poppins font-bold text-xl text-white mb-1">
                        Détails de la réservation
                      </h2>
                      <p className="font-nunito text-sm text-white/90">
                        {formatDate(item.date)}
                      </p>
                    </div>
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Badge statut et montant */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/20">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`px-3 py-1.5 ${getStatusColor(item.status)} border-0`}
                      >
                        <span className="font-nunito text-sm font-semibold">
                          {getStatusLabel(item.status)}
                        </span>
                      </div>
                      <span className="font-nunito text-sm text-white/90">
                        #{item.code?.split("_")[1] || item.id}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="font-poppins font-bold text-2xl text-white">
                        {totalAmount.toFixed(0)} FCFA
                      </div>
                      <div className="font-nunito text-sm text-white/90">
                        {totalServices} service{totalServices > 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contenu scrollable */}
                <div className="flex-1 overflow-y-auto p-6">
                  {/* Services */}
                  <div className="mb-8">
                    <h3 className="font-poppins font-bold text-lg text-gray-900 mb-4">
                      Services
                    </h3>
                    <div className="space-y-3">
                      {item.appointmentServices?.map((service, index) => (
                        <div
                          key={index}
                          className="p-4 bg-gradient-to-r from-gray-50 to-white border border-gray-200"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h4 className="font-poppins font-semibold text-base text-gray-900">
                                {service.service?.nom || "Service inconnu"}
                              </h4>
                              {service.service?.description && (
                                <p className="font-nunito text-sm text-gray-600 mt-1">
                                  {service.service.description}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-poppins font-bold text-lg text-gray-900">
                                {parseFloat(service.unitPrice || 0).toFixed(0)}{" "}
                                FCFA
                              </div>
                              <div className="font-nunito text-xs text-gray-500">
                                x{service.quantity || 1}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Informations client (admin seulement) */}
                  {userIsManager && (
                    <div className="mb-8">
                      <h3 className="font-poppins font-bold text-lg text-gray-900 mb-4">
                        Informations client
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-gradient-to-r from-primary/5 to-pink-500/5 border border-primary/20">
                          <div className="font-nunito text-sm text-gray-500 mb-1">
                            Nom complet
                          </div>
                          <div className="font-poppins font-semibold text-base text-gray-900">
                            {item.client
                              ? `${item.client.nom} ${item.client.prenom}`
                              : item.anonymous_data
                                ? item.anonymous_data.name
                                : "Non renseigné"}
                          </div>
                        </div>
                        <div className="p-4 bg-gradient-to-r from-primary/5 to-pink-500/5 border border-primary/20">
                          <div className="font-nunito text-sm text-gray-500 mb-1">
                            Email
                          </div>
                          <div className="font-poppins font-semibold text-base text-gray-900">
                            {item.client
                              ? item.client.email
                              : item.anonymous_data
                                ? item.anonymous_data.email
                                : "Non renseigné"}
                          </div>
                        </div>
                        <div className="p-4 bg-gradient-to-r from-primary/5 to-pink-500/5 border border-primary/20">
                          <div className="font-nunito text-sm text-gray-500 mb-1">
                            Téléphone
                          </div>
                          <div className="font-poppins font-semibold text-base text-gray-900">
                            {item.client
                              ? item.client.telephone
                              : item.anonymous_data
                                ? item.anonymous_data.telephone
                                : "Non renseigné"}
                          </div>
                        </div>
                        <div className="p-4 bg-gradient-to-r from-primary/5 to-pink-500/5 border border-primary/20">
                          <div className="font-nunito text-sm text-gray-500 mb-1">
                            WhatsApp
                          </div>
                          <div className="font-poppins font-semibold text-base text-gray-900">
                            {item.client
                              ? item.client.whatsapp || item.client.telephone
                              : item.anonymous_data
                                ? item.anonymous_data.telephone
                                : "Non renseigné"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer fixe */}
                <div className="border-t border-gray-200 p-6 bg-white flex-shrink-0">
                  <div className="flex justify-end">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-nunito font-semibold text-sm transition-colors duration-200"
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de confirmation de fin */}
      <AnimatePresence>
        {isCompleteModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setIsCompleteModal(false)}
            />

            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white shadow-2xl w-full max-w-md overflow-hidden"
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-poppins font-bold text-lg text-white">
                        Confirmation
                      </h2>
                      <p className="font-nunito text-sm text-white/90 mt-1">
                        Terminer la réservation
                      </p>
                    </div>
                    <button
                      onClick={() => setIsCompleteModal(false)}
                      className="text-white/80 hover:text-white transition-colors"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Contenu */}
                <div className="p-6">
                  <div className="text-center mb-2">
                    <div className="w-12 h-12 bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <FiCheckCircle className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2">
                      Marquer comme terminé ?
                    </h3>
                    <p className="font-nunito text-sm text-gray-600">
                      Voulez-vous marquer le rendez-vous
                      <span className="font-semibold text-gray-900">
                        {item.client
                          ? `de ${item.client.nom} ${item.client.prenom}`
                          : item.anonymous_data
                            ? `de ${item.anonymous_data.name}`
                            : ""}
                      </span>{" "}
                      comme terminé ? Cette action est irréversible.
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t border-gray-200 p-6">
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setIsCompleteModal(false)}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-nunito font-semibold text-sm transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleComplete}
                      disabled={isCompleteLoading}
                      className={`px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-nunito font-semibold text-sm transition-colors flex items-center space-x-2 ${
                        isCompleteLoading ? "opacity-75 cursor-not-allowed" : ""
                      }`}
                    >
                      {isCompleteLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin" />
                          <span>Chargement...</span>
                        </>
                      ) : (
                        <>
                          <FiCheckCircle className="w-4 h-4" />
                          <span>Confirmer</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal d'annulation (rendez-vous non réglé uniquement) */}
      <AnimatePresence>
        {isCancelModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => !isCancelLoading && setIsCancelModal(false)}
            />

            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white shadow-2xl w-full max-w-md overflow-hidden"
              >
                <div className="bg-gradient-to-r from-rose-500 to-rose-600 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-poppins font-bold text-lg text-white">
                        Annuler le rendez-vous
                      </h2>
                      <p className="font-nunito text-sm text-white/90 mt-1">
                        {formatDate(item.date)}
                      </p>
                    </div>
                    <button
                      onClick={() => setIsCancelModal(false)}
                      disabled={isCancelLoading}
                      className="text-white/80 hover:text-white transition-colors"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <p className="font-nunito text-sm text-gray-600">
                    Voulez-vous annuler le rendez-vous
                    <span className="font-semibold text-gray-900">
                      {item.client
                        ? ` de ${item.client.nom} ${item.client.prenom}`
                        : item.anonymous_data
                          ? ` de ${item.anonymous_data.name}`
                          : ""}
                    </span>{" "}
                    ? Le créneau redeviendra disponible et les employés
                    assignés seront libérés.
                  </p>
                  <p className="font-nunito text-xs text-rose-600 font-semibold">
                    ⚠️ Action irréversible : un rendez-vous annulé ne pourra
                    plus être réglé, assigné ni effectué.
                  </p>
                  <div>
                    <label className="block font-nunito text-sm font-semibold text-gray-700 mb-1">
                      Motif (optionnel)
                    </label>
                    <textarea
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      rows={3}
                      disabled={isCancelLoading}
                      placeholder="Ex : client injoignable, demande du client..."
                      className="w-full px-3 py-2 border border-gray-300 font-nunito text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                    />
                  </div>
                </div>

                <div className="border-t border-gray-200 p-6">
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setIsCancelModal(false)}
                      disabled={isCancelLoading}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-nunito font-semibold text-sm transition-colors"
                    >
                      Retour
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={isCancelLoading}
                      className={`px-4 py-2 bg-rose-500 hover:bg-rose-600 text-white font-nunito font-semibold text-sm transition-colors flex items-center space-x-2 ${
                        isCancelLoading ? "opacity-75 cursor-not-allowed" : ""
                      }`}
                    >
                      {isCancelLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin" />
                          <span>Annulation...</span>
                        </>
                      ) : (
                        <>
                          <FiXCircle className="w-4 h-4" />
                          <span>Confirmer l'annulation</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Modal de reprogrammation */}
      <AnimatePresence>
        {isRescheduleModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => !isRescheduleLoading && setIsRescheduleModal(false)}
            />

            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
              >
                <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="font-poppins font-bold text-lg text-white">
                        Reprogrammer le rendez-vous
                      </h2>
                      <p className="font-nunito text-sm text-white/90 mt-1">
                        Date actuelle : {formatDate(item.date)}
                      </p>
                    </div>
                    <button
                      onClick={() => setIsRescheduleModal(false)}
                      disabled={isRescheduleLoading}
                      className="text-white/80 hover:text-white transition-colors"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="p-6 overflow-y-auto flex-1 space-y-4">
                  <p className="font-nunito text-sm text-gray-600">
                    Choisissez une nouvelle date et un nouveau créneau. Le
                    client sera prévenu par e-mail. Les employés assignés
                    seront désassignés : pensez à réassigner le rendez-vous
                    après la reprogrammation.
                  </p>

                  {newDate ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="font-nunito text-xs text-gray-500">
                          Nouvelle date sélectionnée
                        </p>
                        <p className="font-poppins font-semibold text-gray-900">
                          {newDate}
                        </p>
                      </div>
                      <button
                        onClick={() => setNewDate(null)}
                        disabled={isRescheduleLoading}
                        className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 font-nunito text-xs font-semibold"
                      >
                        Changer
                      </button>
                    </div>
                  ) : (
                    <ReservationServiceCalendar
                      servicesSelected={{
                        durationMinutes: item.estimatedDuration || 30,
                      }}
                      onContinue={(formatted) => setNewDate(formatted)}
                    />
                  )}
                </div>

                <div className="border-t border-gray-200 p-6">
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setIsRescheduleModal(false)}
                      disabled={isRescheduleLoading}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 font-nunito font-semibold text-sm transition-colors"
                    >
                      Retour
                    </button>
                    <button
                      onClick={handleReschedule}
                      disabled={!newDate || isRescheduleLoading}
                      className={`px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-nunito font-semibold text-sm transition-colors flex items-center space-x-2 ${
                        !newDate || isRescheduleLoading
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                    >
                      {isRescheduleLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white animate-spin" />
                          <span>Reprogrammation...</span>
                        </>
                      ) : (
                        <>
                          <FiRefreshCw className="w-4 h-4" />
                          <span>Confirmer la reprogrammation</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

export default ReservationItem;

// // components/ReservationItem.jsx
// import { useState } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import {
//   FiChevronDown,
//   FiChevronUp,
//   FiX,
//   FiUser,
//   FiCalendar,
//   FiClock,
// } from "react-icons/fi";
// import { toast } from "react-toastify";
// import useAuthStore from "../store/authStore";
// import AssignServiceModal from "./admin/AssignServiceModal";
// import axiosToken from "../api/axiosClient";

// function ReservationItem({ item, withAssign = false, temporaryReservation }) {
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isCompleteModal, setIsCompleteModal] = useState(false);
//   const [isCompleteLoading, setIsCompleteLoading] = useState(false);
//   const [isExpanded, setIsExpanded] = useState(item.id == temporaryReservation);
//   const { user, logout } = useAuthStore();

//   const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
//   const [selectedRdv, setSelectedRdv] = useState({});

//   if (!item || !item.id) {
//     return (
//       <div className="p-3 bg-red-50 border border-red-200">
//         <p className="font-nunito text-[14px] text-red-600">
//           Données de réservation incomplètes
//         </p>
//       </div>
//     );
//   }

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     return date.toLocaleDateString("fr-FR", {
//       weekday: "long",
//       year: "numeric",
//       month: "long",
//       day: "numeric",
//       hour: "2-digit",
//       minute: "2-digit",
//     });
//   };

//   const getStatusColor = (status) => {
//     switch (status) {
//       case "PENDING":
//         return "bg-yellow-100 text-yellow-800 border-yellow-200";
//       case "CONFIRMED":
//         return "bg-green-100 text-green-800 border-green-200";
//       case "CANCELLED":
//         return "bg-red-100 text-red-800 border-red-200";
//       case "COMPLETED":
//         return "bg-sky-500/10 text-sky-500 border-sky-500/20";
//       default:
//         return "bg-gray-100 text-gray-800 border-gray-200";
//     }
//   };

//   const getStatusLabel = (status) => {
//     const labels = {
//       PENDING: "En attente",
//       CONFIRMED: "Confirmé",
//       CANCELLED: "Annulé",
//       COMPLETED: "Terminé",
//     };
//     return labels[status] || status;
//   };

//   const handleComplete = async () => {
//     try {
//       setIsCompleteLoading(true);
//       await axiosToken.put(`/reservation/${item.id}/status`, {
//         status: "COMPLETED",
//       });
//       item.status = "COMPLETED";
//       toast.success("Rendez-vous terminé.");
//     } catch (error) {
//       toast.error("Une erreur est survenue, réessayez plus tard.");
//     } finally {
//       setIsCompleteLoading(false);
//       setIsCompleteModal(false);
//     }
//   };

//   const totalServices = item.appointmentServices?.length || 0;
//   const totalAmount = parseFloat(item.totalAmount || 0);

//   return (
//     <>
//       <AssignServiceModal
//         isAssignModalOpen={isAssignModalOpen}
//         closeAssignModal={() => {
//           setSelectedRdv(null);
//           setIsAssignModalOpen(false);
//         }}
//         selectedRdv={selectedRdv}
//       />

//       <motion.li
//         initial={{ opacity: 0, y: 20 }}
//         animate={{ opacity: 1, y: 0 }}
//         exit={{ opacity: 0, y: -20 }}
//         className=" bg-white shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
//       >
//         {/* En-tête de la réservation - Version Mobile */}
//         <div
//           className=" cursor-pointer relative"
//           onClick={() => setIsExpanded(!isExpanded)}
//         >
//           {item.id == temporaryReservation && (
//             <div className="bg-green-200 transition-colors absolute h-full w-full z-10 opacity-30 animate-pulse" />
//           )}

//           <div className="p-2 sm:p-4 flex items-start justify-between gap-3">
//             {/* Côté gauche - Informations principales */}
//             <div className="flex-1 min-w-0">
//               <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
//                 <div className="flex flex-row justify-between sm:items-center gap-2 mb-2">
//                   {/* Badge ID */}
//                   <div className="overflow-hidden md:w-10 md:h-10 md:bg-gradient-to-br md:from-primary md:to-[#FF4081] flex items-center md:justify-center flex-shrink-0">
//                     <span className="text-primary md:text-white font-poppins font-bold text-[16px] text-xs sm:text-sm">
//                       #{item.code?.split("_")[1] || item.id}
//                     </span>
//                   </div>

//                   {/* Chevron mobile */}
//                   <button className="sm:hidden text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0">
//                     {isExpanded ? (
//                       <FiChevronUp size={16} />
//                     ) : (
//                       <FiChevronDown size={16} />
//                     )}
//                   </button>
//                 </div>

//                 <div className="flex-1 min-w-0">
//                   <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
//                     <h3 className="font-poppins font-semibold text-[18px] text-[#222] text-sm sm:text-base truncate flex-row items-center">
//                       <span className="hidden md:inline-flex  mr-1">
//                         Réservation
//                       </span>
//                       <span>{item.code || `#${item.id}`}</span>
//                     </h3>
//                     <div className="flex flex-row items-center justify-between">
//                       <span
//                         className={`px-2 py-1 font-nunito text-[14px] font-semibold border ${getStatusColor(
//                           item.status,
//                         )} w-fit`}
//                       >
//                         {getStatusLabel(item.status)}
//                       </span>

//                       <span className="font-poppins font-bold text-[16px] text-[#111] flex-shrink-0 md:hidden text-xs ">
//                         {totalAmount.toFixed(0)} FCFA
//                       </span>
//                     </div>
//                   </div>

//                   {/* Date - Version différente selon l'écran */}
//                   <div className="hidden sm:block">
//                     <p className="font-nunito text-[14px] text-[#333]">
//                       {formatDate(item.date)}
//                     </p>
//                   </div>
//                   <div className="sm:hidden flex items-center gap-1 font-nunito text-[14px] text-[#333]">
//                     <FiCalendar size={12} />
//                     <span>{formatDate(item.date)}</span>
//                   </div>
//                 </div>
//               </div>

//               {/* Informations supplémentaires mobile */}
//               <div className="sm:hidden flex items-center justify-between mt-2">
//                 <div className="flex justify-between w-full items-center gap-4 font-nunito text-[14px] text-[#333]">
//                   <div className="flex items-center gap-1">
//                     <FiClock size={12} />
//                     <span>
//                       {totalServices} service{totalServices > 1 ? "s" : ""}
//                     </span>
//                   </div>
//                   {item.client && (
//                     <div className="flex items-center gap-1">
//                       <FiUser size={12} />
//                       <span className="truncate max-w-[100px]">
//                         {item.client.prenom} {item.client.nom?.charAt(0)}.
//                       </span>
//                     </div>
//                   )}
//                 </div>
//               </div>
//             </div>

//             {/* Côté droit - Desktop seulement */}
//             <div className="hidden sm:flex items-center gap-4">
//               <div className="text-right">
//                 <p className="font-poppins font-bold text-[16px] text-[#111]">
//                   {totalAmount.toFixed(2)} FCFA
//                 </p>
//                 <p className="font-nunito text-[14px] text-[#333]">
//                   {totalServices} service{totalServices > 1 ? "s" : ""}
//                 </p>
//               </div>

//               <button className="text-gray-400 hover:text-gray-600 transition-colors duration-200">
//                 {isExpanded ? (
//                   <FiChevronUp size={20} />
//                 ) : (
//                   <FiChevronDown size={20} />
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Détails expandables */}
//         <AnimatePresence>
//           {isExpanded && (
//             <motion.div
//               initial={{ opacity: 0, height: 0 }}
//               animate={{ opacity: 1, height: "auto" }}
//               exit={{ opacity: 0, height: 0 }}
//               className="border-t border-gray-200"
//             >
//               <div className="p-3 sm:p-4">
//                 {/* Services résumés */}
//                 <div className="mb-3 sm:mb-4">
//                   <h4 className="font-poppins font-medium text-[15px] text-[#444] mb-2 text-sm sm:text-base">
//                     Services inclus :
//                   </h4>
//                   <div className="space-y-2">
//                     {item.appointmentServices
//                       ?.slice(0, 2)
//                       .map((appointmentService, index) => (
//                         <div
//                           key={index}
//                           className="flex justify-between items-center font-nunito text-[14px] text-sm"
//                         >
//                           <span className="text-[#333] truncate flex-1 mr-2">
//                             {appointmentService.service?.nom ||
//                               "Service inconnu"}
//                           </span>
//                           <span className="font-poppins font-bold text-[16px] text-[#111] flex-shrink-0">
//                             {parseFloat(
//                               appointmentService.unitPrice || 0,
//                             ).toFixed(0)}{" "}
//                             FCFA
//                           </span>
//                         </div>
//                       ))}
//                     {totalServices > 2 && (
//                       <button
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           setIsModalOpen(true);
//                         }}
//                         className="font-nunito font-semibold text-[14px] text-[#FF4081] hover:text-[#FF4081]/90 text-xs sm:text-sm"
//                       >
//                         Voir les {totalServices - 2} services supplémentaires...
//                       </button>
//                     )}
//                   </div>
//                 </div>
//                 {["superadmin", "manager"].includes(user.role.name) &&
//                   item?.assignments.length > 0 && (
//                     <div className="mb-4">
//                       <label className="block font-poppins font-medium text-[15px] text-[#444] mb-2">
//                         Employés Assignés
//                       </label>

//                       <div className="flex flex-wrap gap-2 font-nunito text-[14px] text-xs">
//                         <hr />
//                         {item?.assignments.map((rdv) => (
//                           <div
//                             key={rdv.id}
//                             className="bg-[#FF4081]/10 text-[#FF4081] px-3 py-2 flex items-center gap-2 text-xs"
//                           >
//                             <span className="">
//                               {rdv?.employee?.prenom} {rdv?.employee?.nom}
//                             </span>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}

//                 {/* Actions */}
//                 <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center pt-3 border-t border-gray-100 gap-2 sm:gap-0">
//                   <div className="font-nunito text-[14px] text-[#333] order-2 sm:order-1">
//                     Créé le{" "}
//                     {new Date(item.created_at).toLocaleDateString("fr-FR")}
//                   </div>
//                   <div className="flex gap-2 order-1 sm:order-2">
//                     {["superadmin", "manager"].includes(user.role.name) &&
//                       item.status === "CONFIRMED" && (
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             setIsCompleteModal(true);
//                           }}
//                           className="px-3 py-1 sm:py-2 bg-emerald-400 text-white font-nunito font-semibold text-[14px] hover:bg-emerald-500/90 transition-colors duration-200 flex-1 sm:flex-none"
//                         >
//                           Marquer comme terminé
//                         </button>
//                       )}
//                     {withAssign &&
//                       ["superadmin", "manager"].includes(user.role.name) && (
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             setIsAssignModalOpen(true);
//                             setSelectedRdv(item);
//                           }}
//                           className="px-3 py-1 sm:py-2 bg-[#FF4081] text-white font-nunito font-semibold text-[14px] hover:bg-[#FF4081]/90 transition-colors duration-200 flex-1 sm:flex-none"
//                         >
//                           {item.assignments.length > 0
//                             ? "Réassignation"
//                             : "Assignation"}
//                         </button>
//                       )}
//                     <button
//                       onClick={(e) => {
//                         e.stopPropagation();
//                         setIsModalOpen(true);
//                       }}
//                       className="px-3 py-1 sm:py-2 bg-[#FF4081] text-white font-nunito font-semibold text-[14px] hover:bg-[#FF4081]/90 transition-colors duration-200 flex-1 sm:flex-none"
//                     >
//                       Voir détails
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </motion.div>
//           )}
//         </AnimatePresence>
//       </motion.li>

//       {/* Modal des services - Version Responsive */}
//       <AnimatePresence>
//         {isModalOpen && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
//             <motion.div
//               initial={{ opacity: 0, scale: 0.9 }}
//               animate={{ opacity: 1, scale: 1 }}
//               exit={{ opacity: 0, scale: 0.9 }}
//               className="bg-white w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] overflow-hidden"
//               onClick={(e) => e.stopPropagation()}
//             >
//               {/* En-tête du modal */}
//               <div className="bg-[#FF4081] p-4 sm:p-6">
//                 <div className="flex justify-between items-start gap-4">
//                   <div className="flex-1 min-w-0">
//                     <h2 className="font-poppins font-semibold text-[18px] text-white mb-1 truncate">
//                       Détails de la réservation
//                     </h2>
//                     <p className="font-nunito text-[14px] text-white/80 text-xs sm:text-sm truncate">
//                       {item.code || `#${item.id}`} • {formatDate(item.date)}
//                     </p>
//                   </div>
//                   <button
//                     onClick={() => setIsModalOpen(false)}
//                     className="text-white/80 hover:text-white transition-colors duration-200 p-1 hover:bg-white/10 flex-shrink-0"
//                   >
//                     <FiX size={20} className="sm:w-6 sm:h-6" />
//                   </button>
//                 </div>
//               </div>

//               {/* Contenu du modal */}
//               <div className="p-4 sm:p-6 max-h-[60vh] overflow-y-auto">
//                 {/* Statut et informations */}
//                 <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50">
//                   <div className="flex flex-row gap-3 sm:gap-4 items-center justify-between">
//                     <div className="">
//                       <span
//                         className={`px-3 py-1 font-nunito font-semibold text-[14px] border ${getStatusColor(
//                           item.status,
//                         )}`}
//                       >
//                         {getStatusLabel(item.status)}
//                       </span>
//                     </div>
//                     <div className="text-right">
//                       <p className="font-poppins font-bold text-[16px] text-[#111] text-xl sm:text-2xl">
//                         {totalAmount.toFixed(0)} FCFA
//                       </p>
//                       <p className="font-nunito text-[14px] text-[#333] text-xs sm:text-sm">
//                         {totalServices} service{totalServices > 1 ? "s" : ""}
//                       </p>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Liste des services */}
//                 <div className="mb-4 sm:mb-6">
//                   <h3 className="font-poppins font-semibold text-[18px] text-[#222] mb-3 text-sm sm:text-base">
//                     Services réservés :
//                   </h3>
//                   <div className="space-y-2 sm:space-y-3">
//                     {item.appointmentServices?.map(
//                       (appointmentService, index) => (
//                         <div
//                           key={index}
//                           className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-white border border-gray-200 gap-2 sm:gap-4"
//                         >
//                           <div className="flex-1 min-w-0">
//                             <h4 className="font-poppins font-semibold text-[18px] text-[#222] text-sm sm:text-base">
//                               {appointmentService.service?.nom ||
//                                 "Service inconnu"}
//                             </h4>
//                             {appointmentService.service?.description && (
//                               <p className="font-nunito text-[14px] text-[#333] mt-1 line-clamp-2 text-xs sm:text-sm">
//                                 {appointmentService.service.description}
//                               </p>
//                             )}
//                             <div className="flex flex-wrap gap-2 sm:gap-4 mt-2 font-nunito text-[14px] text-[#333] text-xs">
//                               {/* <span><strong>Durée:</strong> {appointmentService.service?.durationMinutes || 'N/A'} min</span> */}
//                               <span>
//                                 <strong>Quantité:</strong>{" "}
//                                 {appointmentService.quantity || 1}
//                               </span>
//                             </div>
//                           </div>
//                           <div className="text-right flex-shrink-0">
//                             <p className="font-poppins font-bold text-[16px] text-[#111] text-sm sm:text-base">
//                               {parseFloat(
//                                 appointmentService.unitPrice || 0,
//                               ).toFixed(0)}{" "}
//                               FCFA
//                             </p>
//                             <p className="font-nunito text-[14px] text-[#333] text-xs">
//                               unitaire
//                             </p>
//                           </div>
//                         </div>
//                       ),
//                     )}
//                   </div>
//                 </div>

//                 {/* Informations client */}
//                 {["superadmin", "manager"].includes(user.role.name) && (
//                   <div className="p-3 sm:p-4 bg-[#FF4081]/10 border border-[#FF4081]/20">
//                     <h4 className="font-poppins font-medium text-[15px] text-[#444] mb-2 text-sm sm:text-base">
//                       Informations client :
//                     </h4>
//                     <div className="grid grid-cols-1 gap-2 font-nunito text-[14px] text-xs sm:text-sm">
//                       <div>
//                         <span className="font-poppins font-medium text-[15px] text-[#444]">
//                           Nom :
//                         </span>{" "}
//                         {item.client.prenom} {item.client.nom}
//                       </div>
//                       <div>
//                         <span className="font-poppins font-medium text-[15px] text-[#444]">
//                           Email :
//                         </span>{" "}
//                         {item.client.email}
//                       </div>
//                       <div className="grid grid-cols-2 gap-2">
//                         <div>
//                           <span className="font-poppins font-medium text-[15px] text-[#444]">
//                             Téléphone :
//                           </span>{" "}
//                           {item.client.telephone}
//                         </div>
//                         <div>
//                           <span className="font-poppins font-medium text-[15px] text-[#444]">
//                             WhatsApp :
//                           </span>{" "}
//                           {item.client.whatsapp}
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 )}
//               </div>

//               {/* Pied du modal */}
//               <div className="border-t p-3 sm:p-4 bg-gray-50">
//                 <div className="flex justify-end">
//                   <button
//                     onClick={() => setIsModalOpen(false)}
//                     className="px-4 sm:px-6 py-2 bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-200 font-nunito font-semibold text-[14px] w-full sm:w-auto"
//                   >
//                     Fermer
//                   </button>
//                 </div>
//               </div>
//             </motion.div>
//           </div>
//         )}
//       </AnimatePresence>

//       <AnimatePresence>
//         {isCompleteModal && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
//             <motion.div
//               initial={{ opacity: 0, scale: 0.9 }}
//               animate={{ opacity: 1, scale: 1 }}
//               exit={{ opacity: 0, scale: 0.9 }}
//               className="bg-white w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] overflow-hidden"
//               onClick={(e) => e.stopPropagation()}
//             >
//               {/* En-tête du modal */}
//               <div className="bg-emerald-500 p-4 sm:p-6">
//                 <div className="flex justify-between items-start gap-4">
//                   <div className="flex-1 min-w-0">
//                     <h2 className="font-poppins font-semibold text-[18px] text-white mb-1 truncate">
//                       Marquer la prestation comme terminée.
//                     </h2>
//                     <p className="font-nunito text-[14px] text-white/80 text-xs sm:text-sm truncate">
//                       {item.code || `#${item.id}`} • {formatDate(item.date)}
//                     </p>
//                   </div>
//                   <button
//                     onClick={() => setIsCompleteModal(false)}
//                     className="text-white/80 hover:text-white transition-colors duration-200 p-1 hover:bg-white/10 flex-shrink-0"
//                   >
//                     <FiX size={20} className="sm:w-6 sm:h-6" />
//                   </button>
//                 </div>
//               </div>

//               {/* Contenu du modal */}
//               <div className="p-4 sm:p-6 max-h-[60vh] overflow-y-auto">
//                 <span>
//                   Cette action est irréversible. Voulez-vous continuer et
//                   marquer le rendez-vous du client{" "}
//                   <strong>
//                     {item.client.prenom} {item.client.nom}
//                   </strong>{" "}
//                   comme terminé ?
//                 </span>
//               </div>

//               {/* Pied du modal */}
//               <div className="border-t p-3 sm:p-4 bg-gray-50">
//                 <div className="flex justify-end gap-3">
//                   <button
//                     onClick={() => handleComplete()}
//                     className={`${
//                       isCompleteLoading && "animate-pulse"
//                     } px-4 sm:px-6 py-2 bg-emerald-400 text-white hover:bg-emerald-500 transition-colors duration-200 font-nunito font-semibold text-[14px] w-full sm:w-auto`}
//                     disabled={isCompleteLoading}
//                   >
//                     {isCompleteLoading ? "Chargement..." : "Continuer"}
//                   </button>
//                   <button
//                     onClick={() => setIsCompleteModal(false)}
//                     className="px-4 sm:px-6 py-2 bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-200 font-nunito font-semibold text-[14px] w-full sm:w-auto"
//                   >
//                     Fermer
//                   </button>
//                 </div>
//               </div>
//             </motion.div>
//           </div>
//         )}
//       </AnimatePresence>
//     </>
//   );
// }

// export default ReservationItem;
